import { call, put, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { SagaIterator } from 'redux-saga';
import grammarService from '@/services/grammar/grammarService';
import {
    GrammarContent,
    GrammarSession,
    CreateGrammarSessionRequest,
    CompleteGrammarSessionRequest,
} from '@/services/grammar/types';
import {
    fetchGrammarContentRequest,
    fetchGrammarContentSuccess,
    fetchGrammarContentFailure,
    startGrammarSessionRequest,
    startGrammarSessionSuccess,
    startGrammarSessionFailure,
    completeGrammarSessionRequest,
    completeGrammarSessionSuccess,
    completeGrammarSessionFailure,
    submitGrammarAnswerRequest,
    submitGrammarAnswerSuccess,
    submitGrammarAnswerFailure,
    fetchGrammarProgressRequest,
    fetchGrammarProgressSuccess,
    fetchGrammarProgressFailure,
    fetchGrammarFeedbackRequest,
    fetchGrammarFeedbackSuccess,
    fetchGrammarFeedbackFailure,
    fetchGrammarContentsSuccess,
    fetchGrammarContentsFailure,
    fetchGrammarContentsRequest,
} from '../slices/grammar/grammarSlice';
import { ContentFilters } from '@/services/listening/types';

function* fetchGrammarContentsSaga(action: PayloadAction<ContentFilters | undefined>): SagaIterator {
    try {
        const response = yield call(grammarService.getGrammarContents, action.payload);
        yield put(fetchGrammarContentsSuccess(response));
    } catch (error: any) {
        yield put(fetchGrammarContentsFailure(error.message || 'Failed to fetch grammar contents'));
    }
}

// Content Management
function* fetchGrammarContentSaga(action: PayloadAction<string>): SagaIterator {
    try {
        const response: GrammarContent = yield call(grammarService.getGrammarContent, action.payload);
        yield put(fetchGrammarContentSuccess(response));
    } catch (error: any) {
        yield put(fetchGrammarContentFailure(error.message || 'Failed to fetch grammar content'));
    }
}

// Session Management
function* startGrammarSessionSaga(action: PayloadAction<CreateGrammarSessionRequest>): SagaIterator {
    try {
        const response: GrammarSession = yield call(grammarService.startGrammarSession, action.payload);
        yield put(startGrammarSessionSuccess(response));
    } catch (error: any) {
        yield put(startGrammarSessionFailure(error.message || 'Failed to start grammar session'));
    }
}

// Complete Session
function* completeGrammarSessionSaga(action: PayloadAction<{ sessionId: string; data: CompleteGrammarSessionRequest }>): SagaIterator {
    try {
        const { sessionId, data } = action.payload;
        const response: GrammarSession = yield call(grammarService.completeGrammarSession, sessionId, data);
        yield put(completeGrammarSessionSuccess(response));
    } catch (error: any) {
        yield put(completeGrammarSessionFailure(error.message || 'Failed to complete grammar session'));
    }
}

// Answer Submission
function* submitGrammarAnswerSaga(action: PayloadAction<any>): SagaIterator {
    try {
        const response = yield call(grammarService.submitGrammarAnswer, action.payload.sessionId, action.payload.data);
        yield put(submitGrammarAnswerSuccess(response));
    } catch (error: any) {
        yield put(submitGrammarAnswerFailure(error.message || 'Failed to submit answer'));
    }
}

// Progress
function* fetchGrammarProgressSaga(action: PayloadAction<string>): SagaIterator {
    try {
        const response = yield call(grammarService.getSessionProgress, action.payload);
        yield put(fetchGrammarProgressSuccess(response));
    } catch (error: any) {
        yield put(fetchGrammarProgressFailure(error.message || 'Failed to fetch progress'));
    }
}

// Feedback
function* fetchGrammarFeedbackSaga(action: PayloadAction<string>): SagaIterator {
    try {
        const response = yield call(grammarService.getSessionFeedback, action.payload);
        yield put(fetchGrammarFeedbackSuccess(response));
    } catch (error: any) {
        yield put(fetchGrammarFeedbackFailure(error.message || 'Failed to fetch feedback'));
    }
}

// Root Saga
export default function* grammarSaga(): SagaIterator {
    yield takeLatest(fetchGrammarContentsRequest.type, fetchGrammarContentsSaga);
    yield takeLatest(fetchGrammarContentRequest.type, fetchGrammarContentSaga);
    yield takeLatest(startGrammarSessionRequest.type, startGrammarSessionSaga);
    yield takeLatest(completeGrammarSessionRequest.type, completeGrammarSessionSaga);
    yield takeLatest(submitGrammarAnswerRequest.type, submitGrammarAnswerSaga);
    yield takeLatest(fetchGrammarProgressRequest.type, fetchGrammarProgressSaga);
    yield takeLatest(fetchGrammarFeedbackRequest.type, fetchGrammarFeedbackSaga);
}
